import { DateOnly } from '@if/core/date'
import { Icon, tableHeadBand, TooltipIconButton } from '@if/mui'
import { Box, LinearProgress, Skeleton, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import DenseTable from '@src/components/Common/Table/DenseTable'
import { Trash2 } from 'lucide-react'
import { JSX, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MARK_COLUMN_SX } from './EditPaymentRowMark'
import EditPaymentTableRow from './EditPaymentTableRow'
import { EditPaymentBaseline } from './EditPayments-provenance'
import { EditPaymentSchemaType } from './EditPayments-schema'

const PLACEHOLDER_FIELD_HEIGHT = 32

interface EditPaymentTableProps {
  payments: EditPaymentSchemaType[]
  onCriticalChange: (index: number, sortPayments?: boolean) => void
  onUncriticalChange: (index: number, sortPayments?: boolean) => void
  onDelete: (index: number, payment: EditPaymentSchemaType) => void
  onRestore: (index: number, payment: EditPaymentSchemaType) => void
  paymentMethodOptions: JSX.Element[]
  onDeleteAll: () => void
  baseline: EditPaymentBaseline
  loading?: boolean
  placeholderRowCount?: number
  recomputing?: boolean
  disabled?: boolean
  loanActivationDate: DateOnly
}

const EditPaymentTable = ({
  payments,
  onDelete,
  onRestore,
  onCriticalChange,
  onUncriticalChange,
  paymentMethodOptions,
  onDeleteAll,
  baseline,
  loading = false,
  placeholderRowCount = 0,
  recomputing = false,
  disabled = false,
  loanActivationDate,
}: EditPaymentTableProps) => {
  const { t } = useTranslation()

  const paymentRows = useMemo(() => {
    return payments.map((payment, index) => (
      <EditPaymentTableRow
        key={payment.id}
        index={index}
        onDelete={onDelete}
        onRestore={onRestore}
        paymentMethodOptions={paymentMethodOptions}
        onCriticalChange={onCriticalChange}
        onUncriticalChange={onUncriticalChange}
        baseline={baseline}
        disabled={disabled}
        loanActivationDate={loanActivationDate}
      />
    ))
  }, [
    payments,
    onDelete,
    onRestore,
    paymentMethodOptions,
    onCriticalChange,
    onUncriticalChange,
    baseline,
    disabled,
    loanActivationDate,
  ])

  const placeholderRows = useMemo(
    () =>
      Array.from({ length: placeholderRowCount }, (_, index) => (
        <TableRow key={`placeholder-${index}`}>
          <TableCell padding="none" sx={MARK_COLUMN_SX} />
          <TableCell align="center">
            <Skeleton variant="text" width={18} sx={{ mx: 'auto' }} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" height={PLACEHOLDER_FIELD_HEIGHT} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" height={PLACEHOLDER_FIELD_HEIGHT} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" height={PLACEHOLDER_FIELD_HEIGHT} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" height={PLACEHOLDER_FIELD_HEIGHT} />
          </TableCell>
          <TableCell align="center">
            <Skeleton variant="rounded" width={34} height={20} sx={{ mx: 'auto' }} />
          </TableCell>
          <TableCell align="center">
            <Skeleton variant="circular" width={24} height={24} sx={{ mx: 'auto' }} />
          </TableCell>
        </TableRow>
      )),
    [placeholderRowCount],
  )

  return (
    <Box>
      {/* Zero-height so it takes no room in the flow — the bar paints over the top of the header
          band and sticks with it. */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 3, height: 0 }}>
        {recomputing && <LinearProgress aria-label={t('loan.payment.editPayments.recomputing')} />}
      </Box>
      <DenseTable stickyHeader size="small">
        <TableHead sx={(theme) => ({ '& .MuiTableCell-stickyHeader': { backgroundColor: tableHeadBand(theme).bg } })}>
          <TableRow>
            <TableCell padding="none" sx={MARK_COLUMN_SX} aria-label={t('loan.payment.editPayments.marks.column')} />
            <TableCell align="center" sx={{ minWidth: 44 }}>
              {t('loan.payment.editDialog.paymentNumber')}
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>{t('common.date')}</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              {t('loan.payment.amount')}
            </TableCell>
            <TableCell sx={{ minWidth: 170 }}>{t('loan.payment.pmtModeTitle')}</TableCell>
            <TableCell sx={{ minWidth: 150 }}>{t('loan.payment.description')}</TableCell>
            <TableCell align="center" sx={{ width: '1%' }}>
              {t('loan.payment.editPayments.smsColumn')}
            </TableCell>
            <TableCell align="center" sx={{ width: '1%' }}>
              <TooltipIconButton
                title={t('loan.payment.deleteAll')}
                onClick={onDeleteAll}
                size="small"
                color="inherit"
                disabled={disabled || loading}
              >
                <Icon icon={Trash2} size="sm" />
              </TooltipIconButton>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{loading ? placeholderRows : paymentRows}</TableBody>
      </DenseTable>
    </Box>
  )
}

export default memo(EditPaymentTable)
